import {
  type AskPunttMessage,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { type ReviewFiles } from "@mg/schemas/src/commons";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentSteps } from "./AgentSteps";
import { ChatVideoAttachment } from "./ChatVideoAttachment";
import { FileAttachmentButton } from "./FileAttachmentButton";
import { PunttAvatar } from "./PunttAvatar";
import {
  type QuestionAttachment,
  type QuestionFormHandle,
  QuestionForm,
} from "./QuestionForm";
import { SearchProgress } from "./SearchProgress";
import { StructuredResponse } from "./StructuredResponse";
import { ThinkingState } from "./ThinkingState";

import {
  AvatarWithInitials,
  AVATAR_SIZE_BIG,
  AVATAR_SIZE_NORMAL,
} from "../../../components/AvatarWithInitials";
import { Expandable } from "../../../components/Expandable";
import { Markdown } from "../../../components/Markdown";
import { useAnalytics } from "../../../utils/analytics";
import { useSubmitAskPunttFeedback } from "../../../utils/queries/askPuntt";
import {
  MENTION_HIGHLIGHT_CLASS,
  renderMentionSegments,
  resolveMentionActorsForFeedMessage,
} from "../chatUtils";
import {
  computeRawDisplaySegments,
  stabilizeDisplaySegments,
} from "../utils/displaySegments";
import { formatFullDateTime } from "../utils/messageDateGrouping";
import { sanitizeStreamingMarkdown } from "../utils/streamingMarkdown";

/** Short "5:04 PM" timestamp for the feed presentation header. */
function formatFeedTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Image with loading ghost - shows skeleton until image loads, reserves space */
function ImageWithLoader({
  src,
  alt,
  href,
}: {
  src: string;
  alt: string;
  href: string;
}) {
  const { t } = useTranslation("ui");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative block min-h-32 min-w-32 overflow-hidden rounded-xl shadow-sm ring-1 ring-puntt-neutral-gray-5">
      {!loaded && !error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden
        >
          <div className="h-32 w-48 animate-pulse rounded-lg bg-puntt-neutral-gray-11" />
        </div>
      )}
      {error ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-image-attachment-error"
          aria-label={t("chatMessage.openInNewTab", { alt })}
          className="flex h-24 items-center justify-center rounded-lg border border-puntt-neutral-gray-10 bg-puntt-neutral-gray-11/50 px-4 text-puntt-neutral-gray-11"
        >
          <Text size="2">{t("imageFailedToLoadClickToOpen")}</Text>
        </a>
      ) : (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-image-attachment-link"
          aria-label={t("chatMessage.openInNewTab", { alt })}
          className="block"
        >
          <img
            src={src}
            alt={alt}
            className={`max-h-48 max-w-full object-contain transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        </a>
      )}
    </div>
  );
}

interface ChatMessageProps {
  message: AskPunttMessage;
  /** Chat ID for feedback submission */
  chatId?: string;
  /**
   * Ticket review files for this chat's active ticket. When present,
   * video attachments play from the same HLS playlist the ticket uses
   * (`reviewFiles[0].source`) because the raw S3 upload URL is revoked
   * by the ingest pipeline once transcoding finishes.
   */
  reviewFiles?: ReviewFiles[];
  /**
   * Map of attachment S3 key → browser blob URL pointing at the
   * original local file. When present, previews for freshly-uploaded
   * video/image attachments render straight from disk, avoiding the
   * CDN entirely. Only populated for messages sent in the current
   * session; rehydrated messages after a page refresh fall back to
   * the HLS/remote resolution logic.
   */
  localPreviewUrls?: Record<string, string>;
  /** True when the backend is still generating this message */
  isStreaming?: boolean;
  /** True when this is the last assistant message (enables interactive form) */
  isLastAssistant?: boolean;
  /** Called when user submits answers via the multi-question form */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  /** Disable the question form (e.g. while sending) */
  formDisabled?: boolean;
  /** Ref forwarded to the active QuestionForm for external submit triggers */
  questionFormRef?: React.Ref<QuestionFormHandle>;
  /** Called when the form's "has any answer" state changes */
  onAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * When true, suppresses the inline QuestionForm render (it's shown in the
   * input area instead). Non-last messages still render read-only as usual.
   */
  hideInlineQuestionForm?: boolean;
  /**
   * True when the preceding user message carried file attachments. Forwarded
   * to ThinkingState so the pre-agent read pause shows "Reading attachments..."
   * instead of the generic "Starting up...".
   */
  precedingUserHasAttachments?: boolean;
  /**
   * Visual frame for the message. "chat" (default) is the classic chat layout
   * (right-aligned user bubble, left assistant body). "feed" renders a
   * Slack-style row — left avatar + name + role + timestamp header with a
   * left-aligned body — for the Ask Puntt Projects feed, while keeping all the
   * live machinery (streaming, agent steps, canvas, attachments) intact.
   */
  presentation?: "chat" | "feed";
  /** Display name for USER turns in the feed presentation (e.g. the viewer). */
  feedActorName?: string;
  /** Avatar URL for USER turns in the feed presentation. */
  feedActorAvatar?: string | null;
  /**
   * When true in feed presentation, the current viewer's user message gets the
   * gray bubble background. Other members' messages stay unstyled.
   */
  feedOwnUserMessage?: boolean;
  /**
   * Project members for the feed presentation, used to resolve mention ids on
   * a user message to display names when highlighting `@Name` chips.
   */
  feedMembers?: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string | null;
  }[];
  /**
   * Feed-only. When true, the message body is collapsed to a short clamped
   * preview with a "Show more" / "Show less" toggle. Set for every feed message
   * except the most recent (which stays fully expanded as the current focus).
   */
  collapsible?: boolean;
  /**
   * Feed-only. When true (narrow chat column), use an inline avatar+name header
   * instead of a full left avatar column so message text can use full width.
   */
  feedCompactLayout?: boolean;
  /**
   * Feed-only. When false, omit the avatar/name header (continuation in a
   * consecutive-author span). Defaults to true.
   */
  showAuthorHeader?: boolean;
}

function chatMessageTestIds(messageId: string) {
  return {
    assistant: `chat-message-assistant-${messageId}`,
    user: `chat-message-user-${messageId}`,
    content: `chat-message-content-${messageId}`,
    textSegment: (segmentIdx: number) =>
      `chat-message-text-segment-${messageId}-${segmentIdx}`,
  } as const;
}

/**
 * True when a text segment should use the memoized Markdown child.
 * Trailing incomplete streaming segments stay on live Markdown so sanitize
 * can update each tick; completed segments skip re-parse.
 */
export function shouldMemoizeMarkdownSegment(opts: {
  isStreaming: boolean;
  hasContent: boolean;
  isLastSegment: boolean;
}): boolean {
  return !(opts.isStreaming && !opts.hasContent && opts.isLastSegment);
}

/**
 * Memoized markdown for a completed text segment. Content is stable once a
 * segment is no longer the trailing streaming segment, so ReactMarkdown +
 * remark-gfm only re-parse the active trailing segment during a stream.
 */
const MemoizedMarkdownSegment = memo(function MemoizedMarkdownSegment({
  content,
}: {
  content: string;
}) {
  return (
    <Markdown size="2" iconizeEmoji>
      {content}
    </Markdown>
  );
});

function agentStepsSignature(
  steps: AskPunttMessage["agentSteps"] | undefined,
): string {
  if (!steps?.length) return "";
  return `${steps.length}:${steps[steps.length - 1]?.type ?? ""}:${
    steps[steps.length - 1]?.timestamp ?? ""
  }`;
}

/** Exported for unit tests — keep in sync with `memo(ChatMessage, …)`. */
export function areChatMessagePropsEqual(
  prev: ChatMessageProps,
  next: ChatMessageProps,
): boolean {
  if (prev.message !== next.message) {
    // Identity preserved by streaming merge for non-active rows; if the
    // object changed, compare the fields that affect render.
    const a = prev.message;
    const b = next.message;
    if (
      a._id !== b._id ||
      a.status !== b.status ||
      a.content !== b.content ||
      a.streamingContent !== b.streamingContent ||
      a.feedback !== b.feedback ||
      a.canvasContent !== b.canvasContent ||
      a.canvasLoading !== b.canvasLoading ||
      a.viewTicketId !== b.viewTicketId ||
      a.contentBlocks !== b.contentBlocks ||
      agentStepsSignature(a.agentSteps) !== agentStepsSignature(b.agentSteps) ||
      (a.suggestedResponses?.length ?? 0) !==
        (b.suggestedResponses?.length ?? 0) ||
      (a.searchProgress?.length ?? 0) !== (b.searchProgress?.length ?? 0) ||
      (a.attachments?.length ?? 0) !== (b.attachments?.length ?? 0)
    ) {
      return false;
    }
  }
  return (
    prev.chatId === next.chatId &&
    prev.reviewFiles === next.reviewFiles &&
    prev.localPreviewUrls === next.localPreviewUrls &&
    prev.isStreaming === next.isStreaming &&
    prev.isLastAssistant === next.isLastAssistant &&
    prev.onSubmitAnswers === next.onSubmitAnswers &&
    prev.formDisabled === next.formDisabled &&
    prev.questionFormRef === next.questionFormRef &&
    prev.onAnswerChange === next.onAnswerChange &&
    prev.hideInlineQuestionForm === next.hideInlineQuestionForm &&
    prev.precedingUserHasAttachments === next.precedingUserHasAttachments &&
    prev.presentation === next.presentation &&
    prev.feedActorName === next.feedActorName &&
    prev.feedActorAvatar === next.feedActorAvatar &&
    prev.feedOwnUserMessage === next.feedOwnUserMessage &&
    prev.feedMembers === next.feedMembers &&
    prev.collapsible === next.collapsible &&
    prev.feedCompactLayout === next.feedCompactLayout &&
    prev.showAuthorHeader === next.showAuthorHeader
  );
}

function ChatMessageInner({
  message,
  chatId,
  reviewFiles,
  localPreviewUrls,
  isStreaming,
  isLastAssistant,
  onSubmitAnswers,
  formDisabled,
  questionFormRef,
  onAnswerChange,
  hideInlineQuestionForm,
  precedingUserHasAttachments,
  presentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedOwnUserMessage = false,
  feedMembers,
  collapsible = false,
  feedCompactLayout = false,
  showAuthorHeader = true,
}: ChatMessageProps) {
  const { t } = useTranslation("ui");
  const testIds = chatMessageTestIds(message._id);
  const analytics = useAnalytics("ask_puntt_chat");
  const feedbackMutation = useSubmitAskPunttFeedback(chatId ?? "");
  const isCompleted = message.status !== "pending" && !!message.content;
  const showFeedback =
    message.role === "assistant" && isCompleted && !isStreaming && chatId;

  // Display streamingContent while content is being generated, final content once done
  const displayContent = message.content || message.streamingContent || "";
  const hasContent = !!message.content;
  const showStreamingCursor =
    isStreaming && !hasContent && !!message.streamingContent;

  // Append-only segment freeze: Path-2 streaming order must not reshuffle when
  // Path-1 contentBlocks arrive (see displaySegments.stabilizeDisplaySegments).
  const segmentsFreezeRef = useRef<{
    messageId: string;
    segments: ReturnType<typeof computeRawDisplaySegments>;
  }>({ messageId: "", segments: [] });

  const segments = useMemo(() => {
    const raw = computeRawDisplaySegments(
      message.agentSteps,
      displayContent,
      message.contentBlocks,
      { ignoreContentBlocks: !!isStreaming && !hasContent },
    );
    if (segmentsFreezeRef.current.messageId !== message._id) {
      segmentsFreezeRef.current = { messageId: message._id, segments: [] };
    }
    const stabilized = stabilizeDisplaySegments(
      segmentsFreezeRef.current.segments,
      raw,
    );
    segmentsFreezeRef.current.segments = stabilized;
    return stabilized;
  }, [
    message._id,
    message.agentSteps,
    displayContent,
    message.contentBlocks,
    isStreaming,
    hasContent,
  ]);
  const hasSegments = segments.length > 0;
  const hasToolSegment = segments.some((segment) => segment.type === "tools");
  const hasSearchProgress = (message.searchProgress?.length ?? 0) > 0;
  const showStandaloneSearchProgress = hasSearchProgress && !hasToolSegment;
  const showThinkingStatus =
    message.role === "assistant" &&
    !hasToolSegment &&
    !hasSearchProgress &&
    (isStreaming || hasContent || !hasSegments);

  // Streaming cursor fade-out
  const wasStreamingRef = useRef(showStreamingCursor);
  const [cursorFading, setCursorFading] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(showStreamingCursor);

  useEffect(() => {
    if (wasStreamingRef.current && !showStreamingCursor) {
      setCursorFading(true);
      const timer = setTimeout(() => {
        setCursorVisible(false);
        setCursorFading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (showStreamingCursor) {
      setCursorVisible(true);
      setCursorFading(false);
    }
    wasStreamingRef.current = showStreamingCursor;
  }, [showStreamingCursor]);

  // Progressive structured response reveal
  const [revealedSections, setRevealedSections] = useState(0);
  const structuredRevealedRef = useRef(false);

  useEffect(() => {
    if (
      hasContent &&
      message.structuredContent &&
      !structuredRevealedRef.current
    ) {
      structuredRevealedRef.current = true;
      const timers = [0, 150, 300, 500].map((delay, i) =>
        setTimeout(() => setRevealedSections(i + 1), delay),
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [hasContent, message.structuredContent]);

  // Resolve @tokens to actors so feed user messages render highlighted chips.
  const mentionActors = useMemo(
    () =>
      resolveMentionActorsForFeedMessage(
        message.content,
        message.mentions,
        feedMembers,
        message.mentionEmails,
      ),
    [message.content, message.mentions, message.mentionEmails, feedMembers],
  );

  // Split into text vs. attachments so the feed presentation can clamp only the
  // text while always rendering attachments in full (line-clamping an <img>
  // crops it and strands the "Show more" toggle under a sliced picture).
  const userTextContent =
    mentionActors.length > 0 ? (
      <p
        data-testid="chat-message-feed-content"
        className="whitespace-pre-wrap text-sm text-puntt-neutral-gray-12"
      >
        {renderMentionSegments(message.content, mentionActors).map((seg, i) =>
          seg.type === "mention" ? (
            <span
              key={i}
              data-testid={`chat-message-mention-${seg.id}`}
              className={MENTION_HIGHLIGHT_CLASS}
            >
              {seg.label}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </p>
    ) : (
      <Markdown
        size="2"
        iconizeEmoji
        className="text-puntt-neutral-gray-12 [&_strong]:font-semibold [&_strong]:text-puntt-neutral-gray-12"
      >
        {message.content}
      </Markdown>
    );

  const userAttachments =
    message.attachments && message.attachments.length > 0 ? (
      <Flex direction="column" gap="2" className="mt-2">
        {message.attachments.map((att, idx) => {
          const mediaUrl =
            att.source || (att.key ? `https://static.puntt.ai/${att.key}` : "");
          return (
            <div key={att.key ?? idx} className="min-w-0">
              {att.type === "image" && mediaUrl && (
                <ImageWithLoader
                  src={(att.key && localPreviewUrls?.[att.key]) || mediaUrl}
                  alt={att.name ?? "Attachment"}
                  href={mediaUrl}
                />
              )}
              {att.type === "video" && mediaUrl && (
                <ChatVideoAttachment
                  src={
                    (att.key && localPreviewUrls?.[att.key]) ||
                    reviewFiles?.[0]?.source ||
                    mediaUrl
                  }
                  name={att.name}
                />
              )}
              {att.type === "file" && (mediaUrl || att.source) && (
                <FileAttachmentButton
                  url={mediaUrl || att.source || ""}
                  name={att.name}
                />
              )}
            </div>
          );
        })}
      </Flex>
    ) : null;

  const userContent = (
    <>
      {userTextContent}
      {userAttachments}
    </>
  );

  const assistantBody = (
    <div className="min-w-0 space-y-3">
      {/* Interleaved text bubbles and tool step groups */}
      <div data-testid={testIds.content} className="space-y-3">
        {/* Startup/thinking status persists for plain assistant replies. */}
        {showThinkingStatus && (
          <ThinkingState
            message={message}
            precedingUserHasAttachments={precedingUserHasAttachments}
            completed={hasContent && !isStreaming}
          />
        )}

        {showStandaloneSearchProgress && (
          <SearchProgress searchProgress={message.searchProgress ?? []} />
        )}

        {(() => {
          const lastToolIdx = segments.reduce(
            (acc, s, i) => (s.type === "tools" ? i : acc),
            -1,
          );
          return segments.map((segment, idx) => {
            if (segment.type === "tools") {
              return (
                <div key={`tools-${idx}`} className="space-y-3">
                  <AgentSteps
                    steps={segment.steps}
                    isStreaming={
                      !!isStreaming && !hasContent && idx === lastToolIdx
                    }
                    message={message}
                  />
                  {idx === lastToolIdx &&
                    message.searchProgress &&
                    message.searchProgress.length > 0 && (
                      <SearchProgress searchProgress={message.searchProgress} />
                    )}
                </div>
              );
            }
            // Text segment
            const isLastSegment = idx === segments.length - 1;
            const showCursor =
              isLastSegment && (showStreamingCursor || cursorVisible);
            // While the last segment is still streaming, hide raw markdown
            // syntax for the trailing incomplete token (e.g. a half-typed
            // `[label](http...` link) so the stream only reveals plain label
            // text until the token completes and pops into rendered markdown.
            // Once finalized (`hasContent`), render the untouched markdown.
            const segmentContent =
              isStreaming && !hasContent && isLastSegment
                ? sanitizeStreamingMarkdown(segment.content)
                : segment.content;
            // Completed segments get a memoized Markdown child so only the
            // trailing streaming segment re-parses on each tick.
            const memoizeSegment = shouldMemoizeMarkdownSegment({
              isStreaming: !!isStreaming,
              hasContent,
              isLastSegment,
            });
            return (
              <div key={`text-${idx}`} data-testid={testIds.textSegment(idx)}>
                <div className="text-puntt-neutral-gray-12">
                  {memoizeSegment ? (
                    <MemoizedMarkdownSegment content={segmentContent} />
                  ) : (
                    <Markdown size="2" iconizeEmoji>
                      {segmentContent}
                    </Markdown>
                  )}
                  {showCursor && (
                    <span
                      className={`ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-full bg-puntt-accent-9 transition-opacity duration-200 motion-reduce:animate-none ${cursorFading ? "opacity-0" : "opacity-100"}`}
                    />
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Feedback buttons */}
      {showFeedback && (
        <Flex gap="1" className="ml-1">
          <button
            data-testid="chat-feedback-thumbs-up"
            onClick={() => {
              analytics.capture("ask_puntt_feedback", {
                feedback: "thumbs_up",
                message_id: message._id,
                chat_id: chatId,
              });
              feedbackMutation.mutate({
                messageId: message._id,
                feedback: "thumbs_up",
              });
            }}
            className={`rounded-md p-1 transition-colors ${
              message.feedback === "thumbs_up"
                ? "bg-puntt-accent-3 text-puntt-accent-11"
                : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
            }`}
            aria-label={t("thumbsUp")}
          >
            <ThumbsUp
              size={16}
              weight={message.feedback === "thumbs_up" ? "fill" : "regular"}
            />
          </button>
          <button
            data-testid="chat-feedback-thumbs-down"
            onClick={() => {
              analytics.capture("ask_puntt_feedback", {
                feedback: "thumbs_down",
                message_id: message._id,
                chat_id: chatId,
              });
              feedbackMutation.mutate({
                messageId: message._id,
                feedback: "thumbs_down",
              });
            }}
            className={`rounded-md p-1 transition-colors ${
              message.feedback === "thumbs_down"
                ? "bg-puntt-red-3 text-puntt-red-9"
                : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
            }`}
            aria-label={t("thumbsDown")}
          >
            <ThumbsDown
              size={16}
              weight={message.feedback === "thumbs_down" ? "fill" : "regular"}
            />
          </button>
        </Flex>
      )}

      {/* Structured response — only show when final content is set */}
      {hasContent && message.structuredContent && (
        <StructuredResponse
          content={message.structuredContent}
          maxSections={revealedSections}
          messageId={message._id}
        />
      )}

      {/* Multi-question form — hidden for the active message (shown in input area); read-only for history */}
      {hasContent &&
        message.userQuestions &&
        message.userQuestions.length > 0 &&
        !hideInlineQuestionForm && (
          <QuestionForm
            data-testid="chat-message-question-form"
            ref={isLastAssistant ? questionFormRef : undefined}
            questions={message.userQuestions as AskPunttUserQuestion[]}
            onSubmit={onSubmitAnswers ?? (() => {})}
            disabled={formDisabled}
            readOnly={!isLastAssistant}
            hideSubmitButton={isLastAssistant && !!questionFormRef}
            onAnswerChange={isLastAssistant ? onAnswerChange : undefined}
          />
        )}
    </div>
  );

  // Feed presentation: a Slack-style row (left avatar + name + role +
  // timestamp) wrapping the SAME live body, so the project feed keeps full
  // fidelity (streaming, agent steps, canvas, attachments) but reads like the
  // project timeline instead of a centered chat bubble. Narrow columns use a
  // compact inline header (or omit it for consecutive same-author spans).
  if (presentation === "feed") {
    const isUser = message.role === "user";
    const name = isUser ? feedActorName || "Member" : "Puntt";
    const role = isUser ? undefined : "AI Reviewer";
    const time = formatFeedTime(message.createdAt);
    const avatar = isUser ? (
      <AvatarWithInitials
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
        name={name}
        avatar={feedActorAvatar ?? undefined}
      />
    ) : (
      <PunttAvatar
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
      />
    );
    const body = isUser ? (
      <div
        className={
          feedOwnUserMessage
            ? "mt-1 space-y-2 rounded-lg bg-puntt-neutral-gray-3 px-3 py-2"
            : "mt-1 space-y-2"
        }
        data-testid={testIds.content}
      >
        {/* Clamp only the text; attachments always render in full below. */}
        {collapsible ? (
          <Expandable
            maxLines={4}
            size="2"
            allowCollapse
            buttonText="Show more"
            collapseButtonText="Show less"
            testId="feed-message"
          >
            {userTextContent}
          </Expandable>
        ) : (
          userTextContent
        )}
        {userAttachments}
      </div>
    ) : (
      <div className="mt-1">
        {collapsible ? (
          <Expandable
            maxLines={4}
            size="2"
            allowCollapse
            buttonText="Show more"
            collapseButtonText="Show less"
            testId="feed-message"
          >
            {assistantBody}
          </Expandable>
        ) : (
          assistantBody
        )}
      </div>
    );

    if (feedCompactLayout) {
      return (
        <article
          className={
            showAuthorHeader ? "relative mb-4" : "relative mb-2 mt-0.5"
          }
          data-testid={isUser ? testIds.user : testIds.assistant}
          data-role={message.role}
          data-presentation="feed"
          data-feed-compact="true"
          data-author-header={showAuthorHeader ? "true" : "false"}
        >
          {showAuthorHeader ? (
            <div
              className="mb-1 flex min-w-0 items-center gap-2"
              data-testid="feed-message-author-header"
            >
              <div className="shrink-0">{avatar}</div>
              <span className="truncate text-sm font-semibold text-puntt-neutral-gray-12">
                {name}
              </span>
              {role && (
                <span className="shrink-0 text-xs text-puntt-neutral-gray-10">
                  {role}
                </span>
              )}
              {time && (
                <Tooltip content={formatFullDateTime(message.createdAt)}>
                  <span
                    className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                    data-testid="feed-message-timestamp"
                    aria-label={formatFullDateTime(message.createdAt)}
                  >
                    {time}
                  </span>
                </Tooltip>
              )}
            </div>
          ) : null}
          <div className="min-w-0">{body}</div>
        </article>
      );
    }

    return (
      <article
        className="relative mb-4 flex gap-3"
        data-testid={isUser ? testIds.user : testIds.assistant}
        data-role={message.role}
        data-presentation="feed"
      >
        <div
          className="shrink-0 pt-0.5"
          data-testid="feed-message-avatar-column"
        >
          {avatar}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-puntt-neutral-gray-12">
              {name}
            </span>
            {role && (
              <span className="text-xs text-puntt-neutral-gray-10">{role}</span>
            )}
            {time && (
              <Tooltip content={formatFullDateTime(message.createdAt)}>
                <span
                  className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                  data-testid="feed-message-timestamp"
                  aria-label={formatFullDateTime(message.createdAt)}
                >
                  {time}
                </span>
              </Tooltip>
            )}
          </div>
          {body}
        </div>
      </article>
    );
  }

  if (message.role === "user") {
    return (
      <div
        className="relative mb-4"
        data-testid={testIds.user}
        data-role="user"
      >
        <Flex justify="end">
          <div
            className="max-w-[70%] space-y-2 rounded-2xl bg-puntt-neutral-gray-3 px-4 py-3"
            data-testid={testIds.content}
          >
            {userContent}
          </div>
        </Flex>
      </div>
    );
  }

  return (
    <div
      className="relative mb-4"
      data-testid={testIds.assistant}
      data-role="assistant"
    >
      {assistantBody}
    </div>
  );
}

export const ChatMessage = memo(ChatMessageInner, areChatMessagePropsEqual);
